import React, { useState, useEffect } from 'react';
import { FaTimes, FaCreditCard, FaCheck, FaLock } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import apiClient from '../../apiClient';
import './UpgradeModal.css';

// Payment form component that uses Stripe hooks
function PaymentForm({ plan, onSuccess, onClose, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Confirm the setup to save the payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
        return;
      }

      // Payment method saved, now complete the upgrade
      const response = await apiClient.post('/stripe/confirm-upgrade', {
        plan_id: plan.id,
        payment_method_id: setupIntent.payment_method
      });

      if (response.data.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      setError(error.response?.data?.error || 'Failed to upgrade plan. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="payment-section">
        <div className="payment-header">
          <FaCreditCard />
          <h4>Payment Information</h4>
          <FaLock className="security-icon" />
        </div>
        
        <div className="payment-element-container">
          <PaymentElement 
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card'],
              business: { name: 'ezOCM' }
            }}
          />
        </div>
        
        <p className="payment-note">
          Your payment information is securely processed by Stripe. 
          We never store your card details on our servers.
        </p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="modal-footer">
        <button 
          type="button"
          className="cancel-button" 
          onClick={onClose}
          disabled={processing}
        >
          Cancel
        </button>
        <button 
          type="submit"
          className="upgrade-button" 
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : `Upgrade to ${plan.name}`}
        </button>
      </div>
    </form>
  );
}

// Main modal component
export default function UpgradeModalEmbedded({ isOpen, onClose, plan, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    // Load Stripe configuration when modal opens
    const loadStripeConfig = async () => {
      try {
        const response = await apiClient.get('/stripe/config');
        if (response.data.publishable_key) {
          const stripe = loadStripe(response.data.publishable_key);
          setStripePromise(stripe);
        }
      } catch (error) {
        console.error('Failed to load Stripe config:', error);
        setError('Payment system unavailable');
      }
    };

    if (isOpen) {
      loadStripeConfig();
    }
  }, [isOpen]);

  useEffect(() => {
    const initializePayment = async () => {
      if (!plan || !isOpen || !stripePromise) return;
      
      try {
        setError('');
        setLoading(true);
        console.log('Initializing payment for plan:', plan.name);
        
        // Create setup intent for upgrade
        const response = await apiClient.post('/stripe/create-upgrade-payment-intent', {
          plan_id: plan.id
        });
        
        console.log('Setup intent response:', response.data);
        
        if (response.data.setup_intent_client_secret) {
          setClientSecret(response.data.setup_intent_client_secret);
        }
        
      } catch (error) {
        console.error('Failed to initialize payment:', error);
        
        if (error.response?.status === 400) {
          const message = error.response.data.message || error.response.data.error;
          if (message?.includes('customer profile')) {
            setError('Please set up your billing profile first. Contact support for assistance.');
          } else if (message?.includes('not configured')) {
            setError('Payment processing is not available. Please contact support.');
          } else {
            setError(message || 'Failed to initialize payment.');
          }
        } else {
          setError('Failed to initialize payment. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && plan && plan.price_monthly > 0 && stripePromise) {
      initializePayment();
    }
  }, [isOpen, plan, stripePromise]);

  const handleSimpleUpgrade = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/stripe/confirm-upgrade', {
        plan_id: plan.id,
        payment_method_id: 'dev_mode'
      });
      
      if (response.data.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      setError(error.response?.data?.error || 'Failed to upgrade plan.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !plan) return null;

  const showPaymentForm = plan.price_monthly > 0 && clientSecret && stripePromise;
  const isFreePlan = plan.price_monthly === 0;

  // Elements options with client secret
  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#1a73e8',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      },
    },
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content upgrade-modal-embedded" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upgrade to {plan.name}</h2>
          <button className="close-button" onClick={onClose} disabled={loading}>
            <FaTimes />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="plan-summary">
            <h3>{plan.name} Plan</h3>
            <div className="price">
              ${plan.price_monthly}
              <span>/month</span>
            </div>
            
            <div className="features">
              <h4>Features:</h4>
              <ul>
                {plan.features && Object.entries(plan.features).map(([key, value]) => (
                  <li key={key}>
                    <FaCheck className="check-icon" />
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {
                      value === null ? 'Unlimited' : 
                      value === true ? 'Yes' : 
                      value === false ? 'No' : value
                    }
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {error && !showPaymentForm && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {showPaymentForm ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <PaymentForm 
                plan={plan}
                onSuccess={onSuccess}
                onClose={onClose}
                clientSecret={clientSecret}
              />
            </Elements>
          ) : (
            <>
              {loading && !isFreePlan && (
                <div className="payment-notice">
                  <FaCreditCard />
                  <p>Setting up payment method...</p>
                </div>
              )}
              
              {!loading && !isFreePlan && !clientSecret && !error && (
                <div className="payment-notice">
                  <FaCreditCard />
                  <p>Unable to initialize payment. Please check your billing profile or contact support.</p>
                </div>
              )}
              
              {isFreePlan && (
                <div className="payment-notice">
                  <FaCheck />
                  <p>No payment required for the Free plan.</p>
                </div>
              )}
              
              {(isFreePlan || (!loading && !clientSecret)) && (
                <div className="modal-footer">
                  <button 
                    className="cancel-button" 
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="upgrade-button" 
                    onClick={handleSimpleUpgrade}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : `Upgrade to ${plan.name}`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}